import React, { useState, useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { Camera, Mic, Wallet, Plus, Upload, X } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from '@/lib/supabase';
import { useToast } from "@/components/ui/use-toast";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import TransactionForm from '@/components/transactions/TransactionForm';
import VoiceRecorder from '@/components/transactions/VoiceRecorder';
import { Label } from "@/components/ui/label";
import { analyzeReceipt } from '@/lib/openai';
import FullScreenCamera from '@/components/transactions/FullScreenCamera';

interface FloatingActionButtonProps {
  onTransactionSubmit: (data: any) => Promise<void>;
  onWalletSelect: (wallet: any) => void;
  wallets?: any[];
}

const FloatingActionButton: React.FC<FloatingActionButtonProps> = ({
  onTransactionSubmit,
  onWalletSelect,
  wallets: propWallets
}) => {
  const { toast } = useToast();
  const [wallets, setWallets] = useState<any[]>([]);
  const [selectedWallet, setSelectedWallet] = useState<any>(null);
  const [activeSheet, setActiveSheet] = useState<'manual' | 'voice' | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [loading, setLoading] = useState(true);
  const [transcription, setTranscription] = useState<string>('');
  const [parsedTransaction, setParsedTransaction] = useState<any>(null);

  useEffect(() => {
    const fetchWallets = async () => {
      try {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) {
          toast({
            title: "Error",
            description: "Please sign in to continue",
            variant: "destructive",
          });
          return;
        }

        const { data, error } = await supabase
          .from('wallets')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false });

        if (error) throw error;

        if (propWallets) {
          setWallets(propWallets);
        } else {
          setWallets(data || []);
        }

        if (data && data.length > 0) {
          setSelectedWallet(data[0]);
          onWalletSelect(data[0]);
        }
      } catch (error) {
        console.error('Error fetching wallets:', error);
        toast({
          title: "Error",
          description: "Failed to fetch wallets",
          variant: "destructive",
        });
      } finally {
        setLoading(false);
      }
    };

    fetchWallets();
  }, [propWallets, onWalletSelect, toast]);

  const handleCameraClick = () => {
    if (!selectedWallet) {
      toast({
        title: "Error",
        description: "Please select a wallet first",
        variant: "destructive",
      });
      return;
    }
    setShowCamera(true);
  };

  const handlePhotoTaken = async (data: any) => {
    try {
      await onTransactionSubmit(data);
      setShowCamera(false);
    } catch (error) {
      console.error('Error submitting transaction:', error);
      toast({
        title: "Error",
        description: "Failed to submit transaction. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleVoiceTranscription = async (text: string) => {
    setTranscription(text);
    try {
      const response = await fetch('/api/analyze-voice', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ text }),
      });

      if (!response.ok) {
        throw new Error('Failed to analyze voice input');
      }

      const data = await response.json();
      setParsedTransaction(data);
    } catch (error) {
      console.error('Error analyzing voice input:', error);
      toast({
        title: "Error",
        description: "Failed to analyze voice input. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleVoiceSubmit = async () => {
    if (!parsedTransaction) return;

    try {
      await onTransactionSubmit(parsedTransaction);
      setActiveSheet(null);
      setTranscription('');
      setParsedTransaction(null);
    } catch (error) {
      console.error('Error submitting transaction:', error);
      toast({
        title: "Error",
        description: "Failed to submit transaction. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReceiptUpload = async (file: File) => {
    if (!selectedWallet) {
      toast({
        title: "Error",
        description: "Please select a wallet first",
        variant: "destructive",
      });
      return;
    }

    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const base64Image = e.target?.result as string;
        const data = await analyzeReceipt(base64Image);
        await onTransactionSubmit(data);
      };
      reader.readAsDataURL(file);
    } catch (error) {
      console.error('Error processing receipt:', error);
      toast({
        title: "Error",
        description: "Failed to process receipt. Please try again.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      {showCamera && (
        <FullScreenCamera
          onClose={() => setShowCamera(false)}
          onPhotoTaken={handlePhotoTaken}
        />
      )}

      <div className="fixed bottom-4 right-4 flex flex-col gap-2">
        {/* Wallet Selection */}
        <div className="flex items-center gap-2 bg-white dark:bg-gray-800 p-2 rounded-lg shadow-lg">
          <Wallet className="h-4 w-4" />
          <Select
            value={selectedWallet?.id}
            onValueChange={(value) => {
              const wallet = wallets.find(w => w.id === value);
              if (wallet) {
                setSelectedWallet(wallet);
                onWalletSelect(wallet);
              }
            }}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select wallet" />
            </SelectTrigger>
            <SelectContent>
              {wallets.map((wallet) => (
                <SelectItem key={wallet.id} value={wallet.id}>
                  <div className="flex items-center justify-between w-full">
                    <span>{wallet.name}</span>
                    <span className="text-sm text-muted-foreground">
                      {wallet.balance.toLocaleString('id-ID', {
                        style: 'currency',
                        currency: 'IDR',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      })}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Action Buttons */}
        <div className="flex gap-2">
          <Button
            onClick={() => setActiveSheet('manual')}
            className="rounded-full w-12 h-12 bg-[#0B2447] text-white hover:bg-[#0B2447]/90"
            disabled={!selectedWallet}
          >
            <Plus className="h-6 w-6" />
          </Button>

          <Button
            onClick={handleCameraClick}
            className="rounded-full w-12 h-12 bg-[#0B2447] text-white hover:bg-[#0B2447]/90"
            disabled={!selectedWallet}
          >
            <Camera className="h-6 w-6" />
          </Button>

          <Button
            onClick={() => setActiveSheet('voice')}
            className="rounded-full w-12 h-12 bg-[#0B2447] text-white hover:bg-[#0B2447]/90"
            disabled={!selectedWallet}
          >
            <Mic className="h-6 w-6" />
          </Button>

          <input
            type="file"
            ref={fileInputRef}
            className="hidden"
            accept="image/*"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                handleReceiptUpload(file);
              }
            }}
          />
        </div>
      </div>

      {/* Manual Transaction Sheet */}
      <Sheet open={activeSheet === 'manual'} onOpenChange={(open) => !open && setActiveSheet(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Add Transaction</SheetTitle>
          </SheetHeader>
          <TransactionForm 
            onSubmit={onTransactionSubmit}
            onCancel={() => setActiveSheet(null)}
            selectedWallet={selectedWallet}
          />
        </SheetContent>
      </Sheet>

      {/* Voice Transaction Sheet */}
      <Sheet open={activeSheet === 'voice'} onOpenChange={(open) => !open && setActiveSheet(null)}>
        <SheetContent>
          <SheetHeader>
            <SheetTitle>Voice Transaction</SheetTitle>
          </SheetHeader>
          <div className="space-y-4">
            <VoiceRecorder 
              onTranscriptionComplete={handleVoiceTranscription}
              onCancel={() => setActiveSheet(null)}
              selectedWallet={selectedWallet}
            />
            {transcription && (
              <div className="space-y-2">
                <Label>Transcription</Label>
                <p className="text-sm">{transcription}</p>
              </div>
            )}
            {parsedTransaction && (
              <div className="space-y-2">
                <Label>Parsed Transaction</Label>
                <pre className="text-sm">{JSON.stringify(parsedTransaction, null, 2)}</pre>
                <Button onClick={handleVoiceSubmit} className="w-full">
                  Submit Transaction
                </Button>
              </div>
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
};

export default FloatingActionButton; 